import { useMemo, useCallback } from 'react'
import type { FunctionComponent } from 'react'
import {
  Text,
  TDS_DEFAULT_COLORS,
  Link,
  spacing,
} from '@retailer-platform/shared-components/src/tds'
import { ArrowRightIcon } from '@instacart/ids-core'
import { notify, NotificationLevel, clearNotification } from '@retailer-platform/shared-components'
import { IssueIcon } from '@instacart/ids-core'
import { useIntl } from 'react-intl'
import { useOnUnauthorizedRequest } from '../../../legacy/apollo/utils/unauthorizedRequest.utils'
import { useDashMessages } from '../../../intl/intl.hooks'
import { useTruncatedPartnersErrorMessage } from './utils'

export interface PartnerWriteAccessErrorAlertProps {
  partnerNames: string[] | undefined
  partnerIds: number[] | undefined
  isInternal: boolean | undefined
  onOpenBreakGlassModal: (partnerNames: string[], partnerIds: number[]) => void
}

const NOTIFICATION_ID = 'PartnerWriteAccessError'
const SLACK_CHANNEL_NAME = '#ipp-general-support'

export const PartnerWriteAccessErrorAlert: FunctionComponent<PartnerWriteAccessErrorAlertProps> = ({
  partnerNames,
  partnerIds,
  isInternal,
  onOpenBreakGlassModal,
}) => {
  const intl = useIntl()
  const subtitleMessage = useTruncatedPartnersErrorMessage(partnerNames)

  const breakGlassAccessLink = intl.formatMessage({
    id: 'partnerWriteAccess.error.breakGlassAccessLink',
  })

  const canDisplayBreakGlassLink = isInternal && partnerIds?.length > 0

  const onClickLink = useCallback(() => {
    onOpenBreakGlassModal(partnerNames, partnerIds)
    clearNotification(NOTIFICATION_ID)
  }, [onOpenBreakGlassModal, partnerNames, partnerIds])

  const BreakGlassLink = useMemo(
    () => (
      <Link rightIcon={ArrowRightIcon} compact onPress={onClickLink}>
        {breakGlassAccessLink}
      </Link>
    ),
    [breakGlassAccessLink, onClickLink]
  )

  const i18n = useDashMessages(
    {
      title: 'partnerWriteAccess.error.title',
      slackBotAccessMessage: 'partnerWriteAccess.error.subtitleInternal',
      breakGlassDescription: 'partnerWriteAccess.error.breakGlassDescription',
    },
    {
      requestBreakGlassAccessLink: BreakGlassLink,
      slackChannel: SLACK_CHANNEL_NAME,
    }
  )

  return (
    <div>
      <Text typography="bodyEmphasized">{i18n.title}</Text>
      <Text typography="accentRegular">
        {subtitleMessage} {isInternal && i18n.slackBotAccessMessage}
      </Text>
      {canDisplayBreakGlassLink && (
        <Text css={{ marginTop: spacing.s4 }} typography="accentRegular">
          {i18n.breakGlassDescription}
        </Text>
      )}
    </div>
  )
}

export const useNotifyOnPartnerWriteAccessError = (
  isInternal: boolean | undefined,
  onOpenBreakGlassModal: (partnerNames: string[], partnerIds: number[]) => void
) => {
  useOnUnauthorizedRequest('partnerWriteAccessError', ({ partnerNames, partnerIds }) => {
    notify({
      id: NOTIFICATION_ID,
      level: NotificationLevel.Error,
      icon: <IssueIcon fill={TDS_DEFAULT_COLORS.colors.systemDetrimentalRegular} size={28} />,
      contents: (
        <PartnerWriteAccessErrorAlert
          partnerNames={partnerNames}
          partnerIds={partnerIds}
          isInternal={isInternal}
          onOpenBreakGlassModal={onOpenBreakGlassModal}
        />
      ),
      dismissable: true,
      timeout: 30000,
    })
  })
}
